/**
 * Что вкладки сверки в «Оплатах» (D267) спрашивают у `reconcile-api` (D265, порт 8060).
 *
 * Адреса свои, а не `ops-api`: сверка — дополнение к платформе, пакет
 * `reconcile/` в корне. Долги и оплаты сервер берёт из `ops` его
 * публичными функциями; здесь только показ. Суммы — строками в рублях с
 * копейками, даты — «YYYY-MM-DD». Отказ приходит полем `detail`, и
 * человек читает его словами сервера.
 */

import { request, upload } from '../../api';

export type ActStatus = 'ok' | 'issues' | 'partial';
export type ReadState = 'full' | 'partial' | 'failed';

export interface Resolved {
  reason: string;
  by: string | null;
  at: string;
}

export interface OverviewSupplier {
  id: number;
  name: string;
  inn: string | null;
  system_debt: string;
  overdue: string;
  bank_debt: string | null;
  last_act: {
    id: number;
    act_date: string | null;
    status: ActStatus;
    issues: number;
    stale: boolean;
  } | null;
  issues: number;
}

export interface Overview {
  fresh: { statement_to: string | null; statements: number };
  can_edit: boolean;
  suppliers: OverviewSupplier[];
  immediate: { id: number; name: string }[];
}

export interface Statement {
  id: number;
  file_name: string;
  period_from: string;
  period_to: string;
  rows: number;
  new_rows: number;
  debit_total: string;
  uploaded_at: string;
  uploaded_by: string | null;
}

export interface PlanMark {
  invoice_id: number;
  supplier_id: number;
  supplier: string;
  number: string;
  doc_date: string;
  balance: string;
  paid_on: string;
  how: string;
  payment: { day: string; amount: string; purpose: string };
}

export interface PlanPartial {
  invoice_id: number;
  supplier_id: number;
  supplier: string;
  number: string;
  doc_date: string;
  balance: string;
  covered: string;
  left: string;
}

export interface PlanUnused {
  day: string;
  amount: string;
  left: string;
  supplier_id: number;
  supplier: string;
  payee: string;
  purpose: string;
}

export interface Plan {
  summary: {
    open_count: number;
    open_total: string;
    marked_count: number;
    marked_total: string;
    after_count: number;
    after_total: string;
    to_suppliers: number;
    already_used: number;
    period_to: string | null;
  };
  marks: PlanMark[];
  partial: PlanPartial[];
  unused: PlanUnused[];
  unused_older: number;
  unknown: { inn: string; payee: string; count: number; total: string }[];
  learned_inn: { supplier_id: number; supplier: string; inn: string }[];
}

export interface MarkAnswer {
  message: string;
  marked: number;
  total: string;
  errors: string[];
  plan: Plan;
}

export interface PlanAnswer {
  message: string;
  plan: Plan;
}

export type RowKind = 'invoice' | 'bank' | 'mark' | 'act' | 'ghost';

export interface LedgerIssue {
  key: string;
  kind: string;
  text: string;
  resolved: Resolved | null;
}

export interface LedgerRow {
  kind: RowKind;
  key: string;
  date: string;
  title: string;
  number: string | null;
  amount: string;
  sign: '+' | '-' | '';
  balance: string | null;
  status: string | null;
  due: string | null;
  invoice_id: number | null;
  act_id: number | null;
  purpose: string | null;
  issues: LedgerIssue[];
}

export interface LedgerAct {
  id: number;
  number: string | null;
  act_date: string | null;
  period_from: string | null;
  period_to: string | null;
  status: ActStatus;
  issues: number;
  read_state: ReadState;
  file_name: string;
}

export interface Ledger {
  supplier: {
    id: number;
    name: string;
    inn: string | null;
    deferral_days: number | null;
    deferral_source: string | null;
    settles_immediately: boolean;
  };
  debts: {
    system: string;
    bank: string | null;
    bank_since: string | null;
    bank_anchor: 'act' | 'statement' | 'start' | null;
    act: { value: string; act_date: string | null; act_id: number; number: string | null } | null;
    hint: string | null;
  };
  can_edit: boolean;
  period: { from: string; to: string };
  rows: LedgerRow[];
  acts: LedgerAct[];
}

export type LineKind = 'delivery' | 'payment' | 'return' | 'adjustment' | 'other';

export interface ActLine {
  id: number;
  day: string | null;
  kind: LineKind;
  document: string;
  amount: string;
  match: { type: 'invoice' | 'bank'; id: number; label: string } | null;
  issue: LedgerIssue | null;
}

export interface ActExtra {
  key: string;
  kind: string;
  text: string;
  date: string;
  amount: string;
  ref_id: number | null;
  resolved: Resolved | null;
}

export interface ActView {
  id: number;
  supplier_id: number;
  supplier: string;
  number: string | null;
  act_date: string | null;
  period_from: string | null;
  period_to: string | null;
  opening: string | null;
  closing: string | null;
  read_state: ReadState;
  read_note: string | null;
  file_name: string;
  status: ActStatus;
  can_edit: boolean;
  totals: {
    act_closing: string | null;
    our_closing: string | null;
    diff: string | null;
    explained: string;
    unexplained: string | null;
    explained_fully: boolean;
  };
  lines: ActLine[];
  extra: ActExtra[];
  file_url: string | null;
}

const BASE = '/api/reconcile';

function withName(path: string, file: File): string {
  return `${path}?name=${encodeURIComponent(file.name || 'file')}`;
}

/** Полями объекта, как `paymentsApi`: тест подменяет ровно один адрес. */
export const reconcileApi = {
  overview: () => request<Overview>(`${BASE}/overview`),
  statements: () => request<{ statements: Statement[] }>(`${BASE}/statements`),
  uploadStatement: (file: File) =>
    upload<{ statement: Statement; plan: Plan }>(withName(`${BASE}/statements`, file), file),
  plan: () => request<Plan>(`${BASE}/plan`),
  mark: (invoiceIds: number[]) =>
    request<MarkAnswer>(`${BASE}/mark`, { method: 'POST', body: { invoice_ids: invoiceIds } }),
  markOne: (body: { invoice_id: number; amount: string | null; paid_on: string }) =>
    request<PlanAnswer>(`${BASE}/mark-one`, { method: 'POST', body }),
  payee: (body: { inn: string; supplier_id: number }) =>
    request<PlanAnswer>(`${BASE}/payees`, { method: 'POST', body }),
  ledger: (id: number, period: { from: string; to: string }) =>
    request<Ledger>(
      `${BASE}/suppliers/${id}?${new URLSearchParams({ from: period.from, to: period.to }).toString()}`,
    ),
  uploadAct: (supplierId: number, file: File) =>
    upload<{ act: ActView }>(withName(`${BASE}/suppliers/${supplierId}/acts`, file), file),
  act: (id: number) => request<ActView>(`${BASE}/acts/${id}`),
  resolve: (id: number, key: string, reason: string) =>
    request<ActView>(`${BASE}/acts/${id}/resolve`, { method: 'POST', body: { key, reason } }),
  deleteAct: (id: number) =>
    request<{ message: string; supplier_id: number }>(`${BASE}/acts/${id}/delete`, {
      method: 'POST',
    }),
};

/** Перечитывание при возврате на вкладку — как у «Оплат» (§3 ТЗ портала). */
export const LIVE = { refetchOnWindowFocus: true } as const;

/* ── Показ чисел ─────────────────────────────────────────────────────── */

/** «12 345,67» — без знака рубля, для колонок с ₽ в заголовке. */
export function amount(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

/** Сумма строки ленты со знаком: «+12 345,00» у прихода, «−12 345,00» у оплаты. */
export function signed(value: string, sign: '+' | '-' | ''): string {
  const text = amount(value.replace(/^-/, ''));
  if (sign === '+') return `+${text}`;
  if (sign === '-') return `−${text}`;
  return text;
}

/**
 * Сумма выбранного для подписи кнопки — в копейках целыми, чтобы не
 * копить ошибку дробей. Это только подпись: что записать, решает сервер.
 */
export function sumKopecks(values: string[]): string {
  let total = 0;
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) total += Math.round(number * 100);
  }
  const sign = total < 0 ? '-' : '';
  const abs = Math.abs(total);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** «1 расхождение», «2 расхождения», «5 расхождений». */
export function plural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

export function issuesText(n: number): string {
  return `${n} ${plural(n, ['расхождение', 'расхождения', 'расхождений'])}`;
}
